import React from "react";
import Box from "@mui/material/Box";
import Tooltip from "@mui/material/Tooltip";
import { useTranslation } from "react-i18next";
import { useUserSession } from "../../../../hooks/useUserSession";
import { useCurrentUserBranch } from "../../../../hooks/useCurrentUserBranch";
import { getRegionFromUserBranch, Region } from "../../../../utils/regionUtils";

interface RegionFlagIconProps {
  region: Region;
}

const getRegionTranslationKey = (region: Region): string =>
  region === Region.UK ? "appHeader.region.uk" : "appHeader.region.us";

const getRegionNameTranslationKey = (region: Region): string =>
  region === Region.UK ? "appHeader.region.ukName" : "appHeader.region.usName";

const visuallyHiddenSx = {
  position: "absolute",
  width: 1,
  height: 1,
  padding: 0,
  margin: -1,
  overflow: "hidden",
  clip: "rect(0 0 0 0)",
  whiteSpace: "nowrap",
  border: 0
} as const;

const RegionFlagIcon: React.FC<RegionFlagIconProps> = ({ region }) => {
  if (region === Region.UK) {
    return (
      <Box component="svg" viewBox="0 0 28 20" aria-hidden="true" focusable="false" sx={{ width: 22, height: 16 }}>
        <rect width="28" height="20" rx="2" fill="#012169" />
        <path d="M0 0L28 20M28 0L0 20" stroke="#fff" strokeWidth="4" />
        <path d="M0 0L28 20M28 0L0 20" stroke="#C8102E" strokeWidth="2" />
        <path d="M14 0V20M0 10H28" stroke="#fff" strokeWidth="7" />
        <path d="M14 0V20M0 10H28" stroke="#C8102E" strokeWidth="4" />
      </Box>
    );
  }

  return (
    <Box component="svg" viewBox="0 0 28 20" aria-hidden="true" focusable="false" sx={{ width: 22, height: 16 }}>
      <rect width="28" height="20" rx="2" fill="#fff" />
      {Array.from({ length: 7 }).map((_, index) => (
        <rect key={index} y={index * 3} width="28" height="1.6" fill="#B22234" />
      ))}
      <rect width="12" height="9" fill="#3C3B6E" />
      <circle cx="3" cy="2.3" r="0.6" fill="#fff" />
      <circle cx="6" cy="2.3" r="0.6" fill="#fff" />
      <circle cx="9" cy="2.3" r="0.6" fill="#fff" />
      <circle cx="4.5" cy="5" r="0.6" fill="#fff" />
      <circle cx="7.5" cy="5" r="0.6" fill="#fff" />
      <circle cx="3" cy="7.7" r="0.6" fill="#fff" />
      <circle cx="6" cy="7.7" r="0.6" fill="#fff" />
      <circle cx="9" cy="7.7" r="0.6" fill="#fff" />
    </Box>
  );
};

const RegionLabel: React.FC<{ region: Region }> = ({ region }) => {
  const { t } = useTranslation();
  return (
    <Box component="span" aria-hidden="true" sx={{ display: "inline-flex", alignItems: "center", gap: 0.75 }}>
      <RegionFlagIcon region={region} />
      <Box component="span">{t(getRegionTranslationKey(region))}</Box>
    </Box>
  );
};

export const RegionIndicator: React.FC = () => {
  const { t } = useTranslation();
  const { username } = useUserSession();

  const { data: userBranch } = useCurrentUserBranch();

  if (!username) {
    return null;
  }

  const selectedRegion = getRegionFromUserBranch(userBranch);
  const lockedTooltip = String(t("appHeader.region.lockedTooltip"));
  const selectedRegionName = String(t(getRegionNameTranslationKey(selectedRegion)));

  return (
    <Tooltip title={lockedTooltip} arrow={true} describeChild={true}>
      <Box
        component="span"
        data-testid="region-readonly"
        sx={{
          display: "inline-flex",
          alignItems: "center",
          minHeight: 34,
          px: 1.25,
          borderRadius: 1,
          color: "#fff",
          fontWeight: 600
        }}
      >
        <RegionLabel region={selectedRegion} />
        <Box component="span" sx={visuallyHiddenSx}>
          {`${selectedRegionName}. ${lockedTooltip}`}
        </Box>
      </Box>
    </Tooltip>
  );
};
